import { useCallback, useEffect, useRef } from "react"

import { listPlugins } from "@fuse/api/pluginApi"
import { isEmpty } from "lodash-es"
import { useRecoilState } from "recoil"

import { Plugin } from "@fuse/recoil/plugins"
import {
  PluginSearchCacheEntry,
  pluginSearchCacheState,
  pluginSearchState,
  transformPlugins,
} from "@fuse/recoil/pluginSearch"

import { isDev } from "@fuse/utils/DeviceDection"
import { fetchPluginListFromRemoteConfig } from "@fuse/utils/pluginHelpers"

// Cache TTL in milliseconds (30 minutes)
const CACHE_TTL = 30 * 60 * 1000

// Debounce delay in milliseconds
const DEBOUNCE_DELAY = 1500

/**
 * Custom hook for managing plugin search with caching and debouncing
 */
export const usePluginSearch = ({ eventId }: { eventId?: string }) => {
  const [searchState, setSearchState] = useRecoilState(pluginSearchState)
  const [searchCache, setSearchCache] = useRecoilState(pluginSearchCacheState)

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Filter plugins based on search query and category
   */
  const filterPlugins = useCallback(
    (plugins: Plugin[], searchQuery: string, category: string): Plugin[] => {
      let filtered = [...plugins]

      // Apply category filter
      if (category !== "all") {
        filtered = filtered.filter((plugin) => {
          const tags = plugin.manifest?.searchTags || []
          switch (category) {
            case "visual":
              return tags.some((tag) =>
                ["shared_album", "shared_playlist"].includes(tag),
              )
            case "hosting_tools":
              return tags.some((tag) => ["questionnaires"].includes(tag))
            case "experiences":
              return tags.some((tag) => ["rsvp_notes"].includes(tag))
            default:
              return false
          }
        })
      }

      // Apply search filter
      if (searchQuery.trim()) {
        const lowercaseQuery = searchQuery.toLowerCase()
        filtered = filtered.filter((plugin) => {
          const name = plugin.manifest?.name?.toLowerCase() || ""
          const description = plugin.manifest?.description?.toLowerCase() || ""
          const tags = (plugin.manifest?.searchTags || []).map((tag) =>
            tag.toLowerCase(),
          )

          return (
            name.includes(lowercaseQuery) ||
            description.includes(lowercaseQuery) ||
            tags.some((tag) => tag.includes(lowercaseQuery))
          )
        })
      }

      return filtered
    },
    [],
  )

  /**
   * Get cached search results if available and valid
   */
  const getCachedResults = useCallback(
    (searchTerm: string, category: string): Plugin[] | null => {
      const now = Date.now()
      const cacheEntry = searchCache.find(
        (entry) =>
          entry.searchTerm === searchTerm &&
          entry.categoryFilter === category &&
          now - entry.timestamp < CACHE_TTL,
      )

      return cacheEntry ? cacheEntry.plugins : null
    },
    [searchCache],
  )

  /**
   * Update cache with new search results
   */
  const updateCache = useCallback(
    (plugins: Plugin[], searchTerm: string, category: string) => {
      const newEntry: PluginSearchCacheEntry = {
        plugins,
        searchTerm,
        categoryFilter: category,
        timestamp: Date.now(),
      }

      setSearchCache((prev) => {
        // Remove old entries for the same search term and category
        const filtered = prev.filter(
          (entry) =>
            !(
              entry.searchTerm === searchTerm &&
              entry.categoryFilter === category
            ),
        )

        // Add new entry and keep only the last 50 entries
        return [...filtered, newEntry].slice(-50)
      })
    },
    [setSearchCache],
  )

  /**
   * Perform API search with caching
   */
  const performApiSearch = useCallback(
    async (searchTerm: string, category: string, force: boolean = false) => {
      try {
        // Check cache first (unless force is true)
        if (!force) {
          const cachedResults = getCachedResults(searchTerm, category)
          if (cachedResults) {
            console.log(
              "[usePluginSearch] Using cached results for:",
              searchTerm,
              category,
            )
            return cachedResults
          }
        }

        console.log(
          "[usePluginSearch] Making API call for:",
          searchTerm,
          category,
          force ? "(forced)" : "",
        )

        const result = await listPlugins({
          searchTerm: searchTerm || undefined,
          eventId: eventId,
        })

        if (result.success && result.data?.plugins) {
          const plugins = result.data.plugins as Plugin[]

          // Update cache
          updateCache(plugins, searchTerm, category)

          return plugins
        }

        return []
      } catch (error) {
        console.error("[usePluginSearch] Error in API search:", error)
        return []
      }
    },
    [getCachedResults, updateCache],
  )

  /**
   * Update search query with immediate filtering and debounced API call
   */
  const updateSearchQuery = useCallback(
    (query: string) => {
      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Update search query immediately
      setSearchState((prev) => ({
        ...prev,
        searchQuery: query,
        pendingSearchQuery: query,
        // Immediately filter from cached plugins for responsive UI
        displayedPlugins: filterPlugins(
          prev.cachedPlugins,
          query,
          prev.activeCategory,
        ),
      }))

      // Debounced API call - only if no results found in cache
      debounceTimerRef.current = setTimeout(async () => {
        // Check if we have results from cache first
        const cachedResults = filterPlugins(
          searchState.cachedPlugins,
          query,
          searchState.activeCategory,
        )

        // Only make API call if no results found in cache
        if (cachedResults.length === 0 && query.trim()) {
          console.log(
            "[usePluginSearch] No results in cache, making API call for:",
            query,
          )
          setSearchState((prev) => ({ ...prev, isSearching: true }))

          try {
            const apiResults = await performApiSearch(
              query,
              searchState.activeCategory,
            )
            const filteredResults = filterPlugins(
              apiResults,
              query,
              searchState.activeCategory,
            )

            setSearchState((prev) => ({
              ...prev,
              cachedPlugins: apiResults,
              displayedPlugins: filteredResults,
              isSearching: false,
              lastApiCall: Date.now(),
              cacheValid: true,
            }))
          } catch (error) {
            console.error("[usePluginSearch] Error in debounced search:", error)
            setSearchState((prev) => ({ ...prev, isSearching: false }))
          }
        } else {
          console.log(
            "[usePluginSearch] Results found in cache, skipping API call for:",
            query,
          )
        }
      }, DEBOUNCE_DELAY)
    },
    [
      searchState.activeCategory,
      searchState.cachedPlugins,
      filterPlugins,
      performApiSearch,
      setSearchState,
    ],
  )

  /**
   * Update category filter
   */
  const updateCategory = useCallback(
    async (category: string) => {
      setSearchState((prev) => ({
        ...prev,
        isLoading: true,
        activeCategory: category,
      }))

      try {
        const apiResults = await performApiSearch(
          searchState.searchQuery,
          category,
        )
        const filteredResults = filterPlugins(
          apiResults,
          searchState.searchQuery,
          category,
        )

        setSearchState((prev) => ({
          ...prev,
          cachedPlugins: apiResults,
          displayedPlugins: filteredResults,
          isLoading: false,
          activeCategory: category,
          lastApiCall: Date.now(),
          cacheValid: true,
        }))
      } catch (error) {
        console.error("[usePluginSearch] Error updating category:", error)
        setSearchState((prev) => ({ ...prev, isLoading: false }))
      }
    },
    [searchState.searchQuery, filterPlugins, performApiSearch, setSearchState],
  )

  /**
   * Initialize search with initial load
   */
  const initializeSearch = useCallback(async () => {
    if (searchState.cacheValid) return // Already initialized

    setSearchState((prev) => ({ ...prev, isLoading: true }))

    try {
      if (!isDev() && isEmpty(searchState?.cachedPlugins)) {
        const remotePlugins = await fetchPluginListFromRemoteConfig()
        if (remotePlugins && remotePlugins.length > 0) {
          setSearchState((prev) => ({
            ...prev,
            cachedPlugins: transformPlugins(remotePlugins),
            displayedPlugins: transformPlugins(remotePlugins),
          }))
        }
      }
      const apiResults = await performApiSearch("", "all")

      setSearchState((prev) => ({
        ...prev,
        cachedPlugins: apiResults,
        displayedPlugins: apiResults,
        isLoading: false,
        lastApiCall: Date.now(),
        cacheValid: true,
      }))
    } catch (error) {
      console.error("[usePluginSearch] Error initializing search:", error)
      setSearchState((prev) => ({ ...prev, isLoading: false }))
    }
  }, [searchState.cacheValid, performApiSearch, setSearchState])

  /**
   * Reset search state
   */
  const resetSearch = useCallback(() => {
    setSearchState((prev) => ({
      ...prev,
      searchQuery: "",
      pendingSearchQuery: "",
      activeCategory: "all",
      displayedPlugins: prev.cachedPlugins,
    }))
  }, [setSearchState])

  /**
   * Force API search bypassing cache
   */
  const forceApiSearch = useCallback(
    async (searchTerm: string, category: string) => {
      setSearchState((prev) => ({ ...prev, isLoading: true }))

      try {
        const apiResults = await performApiSearch(searchTerm, category, true)
        const filteredResults = filterPlugins(apiResults, searchTerm, category)

        setSearchState((prev) => ({
          ...prev,
          cachedPlugins: apiResults,
          displayedPlugins: filteredResults,
          isLoading: false,
          lastApiCall: Date.now(),
          cacheValid: true,
        }))

        return apiResults
      } catch (error) {
        console.error("[usePluginSearch] Error in forced API search:", error)
        setSearchState((prev) => ({ ...prev, isLoading: false }))
        return []
      }
    },
    [performApiSearch, filterPlugins, setSearchState],
  )

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return {
    // State
    searchState,

    // Actions
    updateSearchQuery,
    updateCategory,
    initializeSearch,
    resetSearch,
    forceApiSearch,

    // Computed
    hasResults: searchState.displayedPlugins.length > 0,
    isLoading: searchState.isLoading || searchState.isSearching,
  }
}
